import type { SubscriberFieldType, SubscriberFieldUi } from './subscriberCustomFields';
import { valueToDraft } from './subscriberCustomFields';
import { channelsToKeyArray } from './channels';
import { subscriberEmailStatus } from './subscriberEmailStatus';
import { subscriberFunnelStatusLabel } from './subscriberFunnelStatus';
import { subscriberSource } from './subscriberSource';
import { subscriberRoleLabel } from './subscriberRoles';
import type { SubscriberRow } from '../components/Subscribers/types';

export type ColumnMode = 'hidden' | 'show' | 'require_value';

export type FieldsLayout = {
  order: string[];
  modes: Record<string, ColumnMode>;
};

export type ListColumnForValue =
  | { kind: 'builtin'; id: string; key: string }
  | { kind: 'custom'; id: string; key: string; fieldType: SubscriberFieldType; ui?: SubscriberFieldUi };

export function isValidFieldStorageKey(key: string): boolean {
  return (
    key.startsWith('builtin:') || (key.startsWith('custom:') && key.length > 'custom:'.length)
  );
}

export function nextColumnMode(current: ColumnMode): ColumnMode {
  if (current === 'hidden') return 'show';
  if (current === 'show') return 'require_value';
  return 'hidden';
}

export function columnModeFor(layout: FieldsLayout, key: string): ColumnMode {
  return layout.modes[key] ?? 'hidden';
}

export function isColumnDisplayed(mode: ColumnMode): boolean {
  return mode === 'show' || mode === 'require_value';
}

export function countDisplayedColumns(layout: FieldsLayout): number {
  return layout.order.filter((key) => isColumnDisplayed(columnModeFor(layout, key))).length;
}

export function countEnforcedColumns(layout: FieldsLayout): number {
  return layout.order.filter((key) => columnModeFor(layout, key) === 'require_value').length;
}

export function buildDefaultModes(
  order: string[],
  defaultShowKeys: string[],
): Record<string, ColumnMode> {
  const showSet = new Set(defaultShowKeys);
  const modes: Record<string, ColumnMode> = {};
  for (const key of order) {
    modes[key] = showSet.has(key) ? 'show' : 'hidden';
  }
  return modes;
}

export function mergeFieldOrder(
  stored: string[],
  available: string[],
  options?: { keepUnknownStored?: boolean },
): string[] {
  const availableSet = new Set(available);
  const storedValid = stored.filter(isValidFieldStorageKey);
  const merged = options?.keepUnknownStored
    ? [...storedValid]
    : storedValid.filter((key) => availableSet.has(key));
  for (const key of available) {
    if (!merged.includes(key)) merged.push(key);
  }
  return merged;
}

function parseModesRecord(raw: unknown, order: string[]): Record<string, ColumnMode> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const modes: Record<string, ColumnMode> = {};
  const keys = new Set([...order, ...Object.keys(source)]);
  for (const key of keys) {
    if (!isValidFieldStorageKey(key)) continue;
    const v = source[key];
    if (v === 'show' || v === 'require_value' || v === 'hidden') {
      modes[key] = v;
    } else if (order.includes(key)) {
      modes[key] = 'hidden';
    }
  }
  return modes;
}

export function normalizeFieldsLayout(
  order: string[],
  modesInput: Record<string, ColumnMode> | null,
  defaultShowKeys: string[],
): FieldsLayout {
  const orderMerged = order;
  let modes = modesInput ? { ...modesInput } : buildDefaultModes(orderMerged, defaultShowKeys);
  for (const key of orderMerged) {
    if (!(key in modes)) modes[key] = 'hidden';
  }
  const orderSet = new Set(orderMerged);
  modes = Object.fromEntries(Object.entries(modes).filter(([key]) => orderSet.has(key)));

  const displayed = orderMerged.filter((key) => isColumnDisplayed(modes[key] ?? 'hidden'));
  if (displayed.length === 0 && orderMerged.length > 0) {
    const fallback = defaultShowKeys.find((key) => orderSet.has(key)) ?? orderMerged[0];
    modes[fallback] = 'show';
  }

  return { order: orderMerged, modes };
}

export function migrateStoredLayout(
  parsed: { order?: unknown; visible?: unknown; modes?: unknown },
  defaultOrder: string[],
  defaultShowKeys: string[],
  options?: { keepUnknownStored?: boolean },
): FieldsLayout | null {
  if (!Array.isArray(parsed.order)) return null;
  const order = mergeFieldOrder(
    parsed.order.filter((key): key is string => typeof key === 'string' && isValidFieldStorageKey(key)),
    defaultOrder,
    options,
  );
  if (order.length === 0) return null;

  const fromModes = parseModesRecord(parsed.modes, order);
  if (fromModes) {
    return normalizeFieldsLayout(order, fromModes, defaultShowKeys);
  }

  if (Array.isArray(parsed.visible)) {
    const visible = parsed.visible.filter(
      (key): key is string => typeof key === 'string' && isValidFieldStorageKey(key),
    );
    if (visible.length > 0) {
      return normalizeFieldsLayout(order, buildDefaultModes(order, visible), defaultShowKeys);
    }
  }

  return null;
}

function customDataForRow(row: SubscriberRow): Record<string, unknown> {
  const cd = row.custom_data;
  if (cd && typeof cd === 'object' && !Array.isArray(cd)) {
    return cd as Record<string, unknown>;
  }
  return {};
}

function bestEmail(row: SubscriberRow): string {
  const e = typeof row.email === 'string' ? row.email.trim() : '';
  if (e) return e;
  const alt = row.email_address ?? row.emailAddress;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

function bestName(row: SubscriberRow): string {
  const n = typeof row.name === 'string' ? row.name.trim() : '';
  if (n) return n;
  const alt = row.full_name ?? row.fullName;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

function parseSubscriberDate(value: unknown): Date | null {
  if (value == null) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True when the row has a non-empty value for this list column. */
export function rowHasColumnValue(row: SubscriberRow, column: ListColumnForValue): boolean {
  if (column.kind === 'builtin') {
    switch (column.id) {
      case 'email':
        return bestEmail(row).length > 0;
      case 'name':
        return bestName(row).length > 0;
      case 'role':
        return subscriberRoleLabel(row.role).length > 0;
      case 'channels':
        return channelsToKeyArray(row.channels).length > 0;
      case 'profile':
        return row.profile_score != null && Number.isFinite(Number(row.profile_score));
      case 'email_status':
        return subscriberEmailStatus(row).length > 0;
      case 'funnel_status':
        return subscriberFunnelStatusLabel(row.funnel_status).length > 0;
      case 'source':
        return subscriberSource(row).length > 0;
      case 'last_contacted_at':
        return parseSubscriberDate(row.last_contacted_at) != null;
      case 'created_at':
        return parseSubscriberDate(row.created_at) != null;
      case 'actions':
        return true;
      default:
        return false;
    }
  }

  const value = customDataForRow(row)[column.id];
  return valueToDraft(value, column.fieldType).trim().length > 0;
}
